import { Capacitor } from '@capacitor/core'

export type AppEnvironment = 'development' | 'preview' | 'staging' | 'production'
export type QuoteMode = 'discount'

// Production is served from the legacy app domain; pwa.* is kept as an alias
// during the domain migration so both hostnames keep hitting the prod API.
const PRODUCTION_HOSTNAMES = new Set(['pwa.thebitcoincompany.com', 'app.thebitcoincompany.com'])
// Dev web deployments talk to the dev API.
const DEVELOPMENT_HOSTNAMES = new Set(['app.dev.thebitcoincompany.com'])
const DEVELOPMENT_API_BASE_URL = 'https://api.dev.thebitcoincompany.com'
const PRODUCTION_API_BASE_URL = 'https://api.thebitcoincompany.com'
const DEVELOPMENT_PUBLIC_ORIGIN = 'https://pwa.thebitcoincompany.com'
const LOCAL_HOSTNAMES = new Set(['localhost', '127.0.0.1', '10.0.2.2'])

function normalizeAbsoluteUrl(value: string | undefined): string | null {
  if (!value) return null

  try {
    return new URL(value).toString().replace(/\/$/, '')
  } catch {
    return null
  }
}

function getBrowserHostname(): string | null {
  if (typeof window === 'undefined') return null
  return window.location.hostname
}

function getBrowserOrigin(): string | null {
  if (typeof window === 'undefined') return null
  return window.location.origin
}

function isLocalHostname(hostname: string | null): boolean {
  return hostname !== null && LOCAL_HOSTNAMES.has(hostname)
}

function isLocalUrl(value: string): boolean {
  try {
    return isLocalHostname(new URL(value).hostname)
  } catch {
    return false
  }
}

export function getAppEnvironment(): AppEnvironment {
  const configuredEnvironment = import.meta.env.VITE_APP_ENV
  if (
    configuredEnvironment === 'development' ||
    configuredEnvironment === 'preview' ||
    configuredEnvironment === 'staging' ||
    configuredEnvironment === 'production'
  ) {
    return configuredEnvironment
  }

  const hostname = getBrowserHostname()
  if (hostname !== null && PRODUCTION_HOSTNAMES.has(hostname)) return 'production'
  if (Capacitor.isNativePlatform()) return 'staging'
  if (isLocalHostname(hostname)) return 'development'
  if (hostname !== null && DEVELOPMENT_HOSTNAMES.has(hostname)) return 'development'
  return 'preview'
}

export function isProductionEnvironment(): boolean {
  return getAppEnvironment() === 'production'
}

export function getApiBaseUrl(): string {
  const configuredApiBaseUrl = normalizeAbsoluteUrl(import.meta.env.VITE_API_BASE_URL)
  if (configuredApiBaseUrl && !(Capacitor.isNativePlatform() && isLocalUrl(configuredApiBaseUrl))) {
    return configuredApiBaseUrl
  }

  if (Capacitor.isNativePlatform()) return DEVELOPMENT_API_BASE_URL

  const hostname = getBrowserHostname()
  if (import.meta.env.DEV && isLocalHostname(hostname)) {
    return getBrowserOrigin() ?? DEVELOPMENT_API_BASE_URL
  }

  return isProductionEnvironment() ? PRODUCTION_API_BASE_URL : DEVELOPMENT_API_BASE_URL
}

export function getPublicOrigin(): string {
  const configuredPublicOrigin = normalizeAbsoluteUrl(import.meta.env.VITE_PUBLIC_ORIGIN)
  if (
    configuredPublicOrigin &&
    !(Capacitor.isNativePlatform() && isLocalUrl(configuredPublicOrigin))
  ) {
    return configuredPublicOrigin
  }

  if (Capacitor.isNativePlatform()) return DEVELOPMENT_PUBLIC_ORIGIN

  return getBrowserOrigin() ?? DEVELOPMENT_PUBLIC_ORIGIN
}

export function isBoltzEmbeddingAllowed(): boolean {
  return false
}
